import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { border_radius } from "src/design-system/tokens/border";
import color from "src/design-system/tokens/color";
import { spacing } from "src/design-system/tokens/spacing";
import { localeToLanguageCode } from "src/utils/conversions/languageCode";
import {
  useSearchInput,
  useSearchKeyboard,
} from "src/utils/hooks/useSearchInput";
import { getPath } from "src/utils/url";
import styled from "styled-components";
import { A11yOutline } from "src/utils/accessibility";
import {
  BlockStyles,
  InputBar,
  InputBars,
  SearchInput,
} from "src/components/SearchInput/SearchInput";
import SwapHorizontal from "src/design-system/icons/SwapHorizontal.svg?react";
import { SearchButton } from "src/components/SearchButton/SearchButton";
import { useRef } from "react";
import { Icon } from "src/design-system/components/Icon/Icon";
import Pin from "src/design-system/icons/Pin.svg?react";
import Dot from "src/design-system/icons/Dot.svg?react";
import { type FocusedElement, FocusContext } from "../../FocusContext";
import type { AutocompletePlace } from "../../api/v1.6/autocomplete";
import useSearch from "../../utils/hooks/useSearch";
import messages from "./LargeSearchBar.messages.ts";
import inputMessages from "./SearchInput/SearchInput.messages.ts";

export function LargeSearchBar() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { origin, destination } = useSearch();
  const keyboard = useSearchKeyboard();
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const originSearch = useSearchInput(
    {
      id: "origin",
      place: origin,
      onSelect: handleOriginAutocompleteSelect,
    },
    keyboard,
  );

  const destinationSearch = useSearchInput(
    {
      id: "destination",
      place: destination,
      onSelect: handleDestinationAutocompleteSelect,
    },
    keyboard,
  );

  function handleSwitch() {
    sendAnalyticsInteractionEvent({
      category: "SearchBox",
      action: "Click:Switch",
    });

    if (origin?.canonicalName === destination?.canonicalName) return;

    destinationSearch.autocompleteState.changeDisplayValueWithoutResults(
      originSearch.autocompleteState.displayValue,
    );

    originSearch.autocompleteState.changeDisplayValueWithoutResults(
      destinationSearch.autocompleteState.displayValue,
    );

    originSearch.setIsResultsClosed(true);
    destinationSearch.setIsResultsClosed(true);
    originSearch.setSelectedPlace(destinationSearch.selectedPlace);
    destinationSearch.setSelectedPlace(originSearch.selectedPlace);
  }

  function handleOriginAutocompleteSelect(place: AutocompletePlace) {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:AutocompleteOption",
    });
  }

  function handleDestinationAutocompleteSelect(place: AutocompletePlace) {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:AutocompleteOption",
    });
  }

  function onFocusChanged(newFocusedElement: FocusedElement) {
    const numResults =
      newFocusedElement.id === "origin"
        ? originSearch.displayResults.length
        : destinationSearch.displayResults.length;
    keyboard.setFocusedElement(newFocusedElement, numResults);
  }

  function handleSearch() {
    navigate({
      pathname: getPath(
        originSearch.selectedPlace?.canonicalName,
        destinationSearch.selectedPlace?.canonicalName,
        localeToLanguageCode(intl.locale),
      ),
      search: window.location.search,
    });
  }
  const originIconLabel = intl.formatMessage(messages.originIconLabel);
  const destinationIconLabel = intl.formatMessage(
    messages.destinationIconLabel,
  );
  const originLabel = intl.formatMessage(inputMessages.originLabel);
  const originPlaceholder = intl.formatMessage(inputMessages.originPlaceholder);
  const destinationLabel = intl.formatMessage(inputMessages.destinationLabel);
  const destinationPlaceholder = intl.formatMessage(
    inputMessages.destinationPlaceholder,
  );

  return (
    <FocusContext
      value={{ focusedElement: keyboard.focusedElement, onFocusChanged }}
    >
      <InputBars>
        <StyledInputBar>
          <SearchInput
            id="origin"
            search={originSearch}
            keyboard={keyboard}
            aria-label={originLabel}
            placeholder={originPlaceholder}
            afterSelectEnter={() => destinationSearch.inputRef.current?.focus()}
            LeftElement={
              <Icon>
                <span className="sr-only">{originIconLabel}</span>
                <Dot color={color.icon.primary} />
              </Icon>
            }
          />

          <SwapButton onClick={handleSwitch}>
            <span className="sr-only">
              {intl.formatMessage(messages.switchLabel)}
            </span>
            <SwapHorizontal color="cod" />
          </SwapButton>

          <SearchInput
            id="destination"
            search={destinationSearch}
            keyboard={keyboard}
            aria-label={destinationLabel}
            placeholder={destinationPlaceholder}
            afterSelectEnter={() => {
              searchButtonRef.current?.focus();
              searchButtonRef.current?.setAttribute(
                "data-focus-visible",
                "true",
              );
            }}
            LeftElement={
              <Icon>
                <span className="sr-only">{destinationIconLabel}</span>
                <Pin color={color.icon.primary} />
              </Icon>
            }
          />
        </StyledInputBar>
        <SearchButton
          onPress={handleSearch}
          ref={searchButtonRef}
          isDisabled={
            origin?.canonicalName ===
              originSearch.selectedPlace?.canonicalName &&
            destination?.canonicalName ===
              destinationSearch.selectedPlace?.canonicalName
          }
        />
      </InputBars>
    </FocusContext>
  );
}

const StyledInputBar = styled(InputBar)`
  flex: 1;
`;

const SwapButton = styled.button`
  ${A11yOutline}
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing.md};
  box-sizing: border-box;
  width: ${spacing.xxxxl};
  height: ${spacing.xxxxl};
  border-radius: ${border_radius.rounded_md};
  background-color: ${color.bg.surface.active};
  border: 1px solid ${color.border.secondary};
  cursor: pointer;
  &:hover {
    background-color: ${color.input.bgSurfaceActive};
  }

  ${BlockStyles}
  padding: unset;
`;
